import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadProps } from 'antd'
import { DownloadOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons'
import {
  ORDERS_IMPORT_EXAMPLE,
  ORDERS_IMPORT_HEADERS,
  downloadXlsxTemplate,
} from '../../../lib/import-xlsx-template'
import type {
  ImportOfficeTarget,
  ImportResultPayload,
  OfficeSelectOption,
  OrdersImportAnalyzeData,
  OrdersImportResolutions,
} from '../../../lib/import-upload'
import {
  ORDERS_IMPORT_COLUMN_HINTS,
  analyzeOrdersImport,
  commitOrdersImport,
  officeOptionKey,
} from '../../../lib/import-upload'
import { OrdersImportWizard } from '../orders-import-wizard'

const { Text } = Typography
const { Dragger } = Upload

type Props = {
  officeOptions: OfficeSelectOption[]
  onImported?: () => void
  variant?: 'inline' | 'modal'
  buttonType?: 'default' | 'primary' | 'dashed' | 'link' | 'text'
  block?: boolean
  disabled?: boolean
}

type Phase = 'upload' | 'wizard' | 'result'

function OrdersImportPanel({
  officeOptions,
  onImported,
  emptyOfficeMessage = 'Импорт заказов доступен для офисов организаций типа «Дилер» или «Головной офис»',
}: {
  officeOptions: OfficeSelectOption[]
  onImported?: () => void
  emptyOfficeMessage?: string
}) {
  const [phase, setPhase] = useState<Phase>('upload')
  const [selectedTarget, setSelectedTarget] = useState<ImportOfficeTarget | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [analysis, setAnalysis] = useState<OrdersImportAnalyzeData | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResultPayload | null>(null)

  const selectOptions = useMemo(
    () =>
      officeOptions.map(opt => ({
        value: officeOptionKey(opt),
        label: opt.label,
      })),
    [officeOptions],
  )

  React.useEffect(() => {
    if (officeOptions.length) {
      setSelectedTarget({
        organizationId: officeOptions[0].organizationId,
        officeId: officeOptions[0].officeId,
      })
    } else {
      setSelectedTarget(null)
    }
  }, [officeOptions])

  const downloadTemplate = useCallback(() => {
    downloadXlsxTemplate(
      'import-orders-template.xlsx',
      ORDERS_IMPORT_HEADERS,
      ORDERS_IMPORT_EXAMPLE,
      'Заказы',
    )
  }, [])

  const resetToUpload = useCallback(() => {
    setPhase('upload')
    setFile(null)
    setAnalysis(null)
    setUploadError(null)
    setResult(null)
    setProgress(0)
  }, [])

  const runCommit = useCallback(
    async (resolutions: OrdersImportResolutions, sourceFile: File, officeId: number) => {
      setUploading(true)
      setProgress(0)
      setUploadError(null)
      try {
        const response = await commitOrdersImport(sourceFile, officeId, resolutions, percent =>
          setProgress(percent),
        )
        setResult(response)
        setPhase('result')
        const errCount = response.errors?.length || 0
        if (errCount > 0) {
          message.warning(`Импорт завершён с ошибками: ${errCount}`)
        } else {
          message.success('Импорт успешно выполнен')
        }
        onImported?.()
      } catch (err) {
        const msg = (err as Error)?.message || 'Ошибка импорта'
        setUploadError(msg)
        message.error(msg)
      } finally {
        setUploading(false)
      }
    },
    [onImported],
  )

  const customRequest: UploadProps['customRequest'] = options => {
    const uploaded = options.file as File
    const { onSuccess: antSuccess, onError } = options

    if (!selectedTarget) {
      message.error('Сначала выберите офис')
      onError?.(new Error('office required'))
      return
    }

    setUploading(true)
    setProgress(0)
    setUploadError(null)
    setResult(null)
    setFile(uploaded)

    void (async () => {
      try {
        const data = await analyzeOrdersImport(uploaded, selectedTarget.officeId, percent =>
          setProgress(percent),
        )
        antSuccess?.(data, uploaded)
        setAnalysis(data)
        if (data.needsWizard) {
          setPhase('wizard')
        } else {
          await runCommit({}, uploaded, selectedTarget.officeId)
        }
      } catch (err) {
        const msg = (err as Error)?.message || 'Ошибка проверки файла'
        setUploadError(msg)
        onError?.(err as Error)
        message.error(msg)
      } finally {
        setUploading(false)
      }
    })()
  }

  const beforeUpload = (nextFile: File) => {
    const name = nextFile.name.toLowerCase()
    const ok = name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv')
    if (!ok) {
      message.error('Допустимы файлы .xls, .xlsx или .csv')
      return Upload.LIST_IGNORE
    }
    return true
  }

  if (!officeOptions.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyOfficeMessage} />
  }

  if (phase === 'wizard' && analysis && file && selectedTarget) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {uploadError && (
          <Alert
            type="error"
            showIcon
            closable
            message="Не удалось импортировать"
            description={uploadError}
            onClose={() => setUploadError(null)}
          />
        )}
        <OrdersImportWizard
          analysis={analysis}
          submitting={uploading}
          onCancel={resetToUpload}
          onCommit={resolutions => {
            void runCommit(resolutions, file, selectedTarget.officeId)
          }}
        />
      </Space>
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Text strong>Офис для импорта</Text>
        <Select
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Выберите офис"
          value={selectedTarget ? officeOptionKey(selectedTarget) : undefined}
          options={selectOptions}
          onChange={value => {
            const [orgId, officeId] = String(value).split(':').map(Number)
            setSelectedTarget({ organizationId: orgId, officeId })
            resetToUpload()
          }}
        />
      </div>

      <Space
        align="center"
        wrap
        size={8}
        style={{ width: '100%', justifyContent: 'space-between' }}
      >
        <Text type="secondary">
          Загрузите Excel или CSV. Система сначала проверит файл. Если каких-то сотрудников или
          стадий нет — подскажет, что сделать.
        </Text>
        <Button size="small" icon={<DownloadOutlined />} onClick={downloadTemplate}>
          Скачать пример .xlsx
        </Button>
      </Space>

      <Collapse
        size="small"
        ghost
        items={[
          {
            key: 'columns',
            label: 'Колонки шаблона',
            children: (
              <Table
                size="small"
                pagination={false}
                rowKey="name"
                dataSource={ORDERS_IMPORT_COLUMN_HINTS}
                columns={[
                  {
                    title: 'Колонка',
                    dataIndex: 'name',
                    width: 180,
                    render: (name: string, row: { required?: boolean }) => (
                      <Text>
                        {name}
                        {row.required ? <Text type="danger"> *</Text> : null}
                      </Text>
                    ),
                  },
                  { title: 'Описание', dataIndex: 'description' },
                ]}
              />
            ),
          },
        ]}
      />

      {!selectedTarget && (
        <Alert type="warning" showIcon message="Выберите офис, чтобы загрузить файл." />
      )}

      <Dragger
        name="file"
        multiple={false}
        accept=".xls,.xlsx,.csv"
        showUploadList={false}
        disabled={uploading || !selectedTarget}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        style={{ padding: 0 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '10px 12px',
            textAlign: 'left',
          }}
        >
          <InboxOutlined style={{ fontSize: 26, color: '#1677ff' }} />
          <div>
            <div style={{ fontSize: 14 }}>Нажмите или перетащите файл сюда</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Поддерживаются .xls, .xlsx и .csv</div>
          </div>
        </div>
      </Dragger>

      {uploading && (
        <div>
          <Text>{phase === 'result' ? 'Импорт…' : 'Проверяем файл…'}</Text>
          <Progress percent={progress} status="active" />
        </div>
      )}

      {uploadError && (
        <Alert
          type="error"
          showIcon
          closable
          message="Ошибка"
          description={uploadError}
          onClose={() => setUploadError(null)}
        />
      )}

      {result && (
        <>
          <Alert
            showIcon
            type={(result.errors?.length || 0) > 0 ? 'warning' : 'success'}
            message={`Обработано успешно: ${result.processed ?? result.successful ?? 0} из ${
              result.count ?? 0
            }`}
            description={
              (result.errors?.length || 0) > 0
                ? `Ошибок: ${result.errors?.length}. Строки с ошибками не созданы.`
                : 'Все строки обработаны без ошибок.'
            }
          />
          {result.errors && result.errors.length > 0 && (
            <div
              style={{
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid #ffccc7',
                borderRadius: 8,
                padding: '8px 12px',
                background: '#fff2f0',
              }}
            >
              <Text type="danger" strong>
                Ошибки по строкам
              </Text>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {result.errors.map((error, index) => (
                  <li key={`${error.rowNumber}-${index}`}>
                    <Text type="danger">
                      Строка {error.rowNumber}: {error.message}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button onClick={resetToUpload}>Загрузить другой файл</Button>
        </>
      )}
    </Space>
  )
}

export function ImportOrdersBlock({
  officeOptions,
  onImported,
  variant = 'inline',
  buttonType = 'primary',
  block = false,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false)

  if (variant === 'inline') {
    return <OrdersImportPanel officeOptions={officeOptions} onImported={onImported} />
  }

  return (
    <>
      <Button
        type={buttonType}
        block={block}
        disabled={disabled}
        icon={<UploadOutlined />}
        onClick={() => setOpen(true)}
      >
        Импорт из Excel
      </Button>
      <Modal
        open={open}
        title="Импорт заказов"
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="close" onClick={() => setOpen(false)}>
            Закрыть
          </Button>,
        ]}
        width={720}
        destroyOnClose
      >
        <OrdersImportPanel
          officeOptions={officeOptions}
          onImported={() => {
            onImported?.()
          }}
        />
      </Modal>
    </>
  )
}

export default ImportOrdersBlock
